import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Search, X, Plus, ExternalLink, CreditCard } from "lucide-react";
import { T } from "./clientsTokens";

const BILLING = `${process.env.REACT_APP_BACKEND_URL}/api/billing`;

/* Links Stripe customers to a client. Writes through POST /billing/match, which
   makes the link sticky, cascades it onto that customer's subscriptions and
   invoices and re-runs the overdue chases — so it takes effect immediately,
   independently of the surrounding form's Save. */
export const StripeCustomerPicker = ({ clientId, clientName }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const searchRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${BILLING}/customers/picker`, { params: { client_id: clientId } });
      setRows(r.data?.customers || []);
      setFailed(false);
    } catch { setFailed(true); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (clientId) load(); /* eslint-disable-next-line */ }, [clientId]);
  useEffect(() => { if (open) setTimeout(() => searchRef.current?.focus(), 50); }, [open]);

  const linked = useMemo(() => rows.filter((r) => r.linked_here), [rows]);
  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const matches = rows
      .filter((r) => !r.linked_here)
      .filter((r) => !needle
        || (r.name || "").toLowerCase().includes(needle)
        || (r.email || "").toLowerCase().includes(needle)
        || (r.id || "").toLowerCase().includes(needle));
    // Capped only to keep the list scrollable. Searching looks at every customer,
    // so a hidden one can always be found by typing rather than vanishing.
    return { rows: matches.slice(0, 40), hidden: Math.max(0, matches.length - 40) };
  }, [rows, q]);

  const link = async (row) => {
    if (row.linked_elsewhere
        && !window.confirm(`${row.name || row.id} is already billed to ${row.client_name}.\n\n`
                           + `Move it to ${clientName || clientId}? Their subscriptions and invoices `
                           + `will move with it.`)) return;
    setBusy(row.id);
    try {
      await axios.post(`${BILLING}/match`, {
        customer_id: row.id, client_id: clientId, reassign: !!row.linked_elsewhere,
      });
      toast.success(`${row.name || row.id} is now billed to ${clientName || clientId}`);
      setQ(""); setOpen(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not link that customer"); }
    finally { setBusy(""); }
  };

  const unlink = async (row) => {
    setBusy(row.id);
    try {
      await axios.post(`${BILLING}/match`, { customer_id: row.id, client_id: null });
      toast.success(`${row.name || row.id} unlinked`, {
        description: "It stays in Stripe. Nothing was cancelled or refunded.",
      });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not unlink that customer"); }
    finally { setBusy(""); }
  };

  if (!clientId) return null;

  return (
    <div className="mt-1" data-testid="stripe-customer-picker">
      {loading ? (
        <div className="flex items-center gap-2 text-[12px] py-1.5" style={{ color: T.ink3 }}>
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading the Stripe mirror…
        </div>
      ) : failed ? (
        <div className="text-[12px] py-1.5" style={{ color: T.ink3 }} data-testid="stripe-picker-failed">
          Billing is not reachable, so customers cannot be linked right now.
        </div>
      ) : (
        <>
          {linked.length === 0 && !open && (
            <div className="text-[12px] py-1" style={{ color: T.ink3 }} data-testid="stripe-picker-none">
              No Stripe customer linked — nothing is billed to this client yet.
            </div>
          )}

          {linked.map((row) => (
            <div key={row.id} data-testid={`stripe-linked-${row.id}`}
                 className="flex items-start gap-2 rounded-md border px-2 py-1.5 mb-1"
                 style={{ borderColor: "#DAD5CA", background: "#FBFAF7" }}>
              <CreditCard className="h-3.5 w-3.5 mt-[2px] shrink-0" style={{ color: T.ink3 }} />
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-semibold truncate" style={{ color: T.ink }}>
                  {row.name || row.id}
                  {row.matched_by && row.matched_by !== "manual" && (
                    <span className="ml-1.5 text-[10px] font-bold uppercase tracking-[0.06em]"
                          style={{ color: T.ink3 }}>matched by {row.matched_by.replace("_", " ")}</span>
                  )}
                </div>
                <div className="text-[11px] truncate" style={{ color: T.ink3 }}>{row.sublabel}</div>
              </div>
              <a href={row.dashboard_url} target="_blank" rel="noreferrer" title="Open in Stripe"
                 data-testid={`stripe-open-${row.id}`}
                 className="shrink-0 p-1 rounded hover:bg-[#EFEBE2]"><ExternalLink className="h-3.5 w-3.5" style={{ color: T.ink3 }} /></a>
              <button type="button" onClick={() => unlink(row)} disabled={busy === row.id}
                      title="Unlink from this client" data-testid={`stripe-unlink-${row.id}`}
                      className="shrink-0 p-1 rounded hover:bg-[#EFEBE2] disabled:opacity-50">
                {busy === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                 : <X className="h-3.5 w-3.5" style={{ color: T.ink3 }} />}
              </button>
            </div>
          ))}

          {!open ? (
            <button type="button" onClick={() => setOpen(true)} data-testid="stripe-picker-open"
                    className="inline-flex items-center gap-1 text-[12px] font-semibold mt-0.5"
                    style={{ color: "#1F6FEB" }}>
              <Plus className="h-3.5 w-3.5" /> Link a Stripe customer
            </button>
          ) : (
            <div className="rounded-md border mt-1" style={{ borderColor: "#DAD5CA" }}>
              <div className="flex items-center gap-1.5 px-2 border-b" style={{ borderColor: "#EFEBE2" }}>
                <Search className="h-3.5 w-3.5 shrink-0" style={{ color: T.ink3 }} />
                <input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)}
                       data-testid="stripe-picker-search" placeholder="Search by name, email or cus_…"
                       className="h-8 flex-1 bg-transparent text-[12.5px] outline-none" />
                <button type="button" onClick={() => { setOpen(false); setQ(""); }}
                        data-testid="stripe-picker-close"
                        className="p-1 rounded hover:bg-[#EFEBE2]"><X className="h-3.5 w-3.5" style={{ color: T.ink3 }} /></button>
              </div>
              <div className="max-h-52 overflow-y-auto">
                {results.rows.length === 0 ? (
                  <div className="px-2.5 py-2 text-[12px]" style={{ color: T.ink3 }}>
                    {q ? "No Stripe customer matches that." : "Every Stripe customer is already linked."}
                  </div>
                ) : results.rows.map((row) => (
                  <button key={row.id} type="button" onClick={() => link(row)} disabled={!!busy}
                          data-testid={`stripe-pick-${row.id}`}
                          className="w-full text-left px-2.5 py-1.5 border-b last:border-b-0 hover:bg-[#F6F4EF] disabled:opacity-50"
                          style={{ borderColor: "#F2EFE8" }}>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[12.5px] font-semibold truncate"
                            style={{ color: row.linked_elsewhere ? T.ink3 : T.ink }}>
                        {row.name || row.id}
                      </span>
                      {row.linked_elsewhere && (
                        <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.06em] px-1 py-[1px] rounded"
                              style={{ color: "#8A7A4E", background: "#FFF6DB" }}>
                          linked to {row.client_name}
                        </span>
                      )}
                      {busy === row.id && <Loader2 className="h-3 w-3 animate-spin" />}
                    </div>
                    <div className="text-[11px] truncate" style={{ color: T.ink3 }}>{row.sublabel}</div>
                  </button>
                ))}
                {results.hidden > 0 && (
                  <div className="px-2.5 py-1.5 text-[11.5px]" style={{ color: T.ink3 }}
                       data-testid="stripe-picker-more">
                    {results.hidden} more — type to narrow the list.
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
